import * as React from 'react';
import { cn } from '@/lib/utils';

type Badge3DVariant = 'spicy' | 'new' | 'popular' | 'riceless' | 'scorched' | 'mini';

const variantConfig: Record<Badge3DVariant, { bg: string; shadow: string; glare: string; label: string }> = {
  spicy: {
    bg: 'bg-[#E04458]',
    shadow: '0 3px 8px rgba(224,68,88,0.45), inset 0 -2px 4px rgba(0,0,0,0.18)',
    glare: 'rgba(255,255,255,0.3)',
    label: 'Острое',
  },
  new: {
    bg: 'bg-[#F472B6]',
    shadow: '0 3px 8px rgba(244,114,182,0.45), inset 0 -2px 4px rgba(0,0,0,0.15)',
    glare: 'rgba(255,255,255,0.35)',
    label: 'Новинка',
  },
  popular: {
    // Фирменный фиолетовый читается из токена, а не переписан числом:
    // #7B42FF === var(--primary) в обеих темах (ответ 44 = А). Значение то же,
    // но правка палитры теперь доедет и сюда.
    bg: 'bg-[var(--primary)]',
    shadow: '0 3px 8px rgba(123,66,255,0.45), inset 0 -2px 4px rgba(0,0,0,0.18)',
    glare: 'rgba(255,255,255,0.3)',
    label: 'Хит',
  },
  riceless: {
    bg: 'bg-[#2DD4BF]',
    shadow: '0 3px 8px rgba(45,212,191,0.45), inset 0 -2px 4px rgba(0,0,0,0.15)',
    glare: 'rgba(255,255,255,0.35)',
    label: 'Без риса',
  },
  scorched: {
    bg: 'bg-[#FBBF24]',
    shadow: '0 3px 8px rgba(251,191,36,0.45), inset 0 -2px 4px rgba(0,0,0,0.15)',
    glare: 'rgba(255,255,255,0.35)',
    label: 'Опалённый',
  },
  mini: {
    bg: 'bg-[#22D3EE]',
    shadow: '0 3px 8px rgba(34,211,238,0.45), inset 0 -2px 4px rgba(0,0,0,0.15)',
    glare: 'rgba(255,255,255,0.35)',
    label: 'Мини',
  },
};

export interface Badge3DProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant: Badge3DVariant;
  size?: 'sm' | 'default';
}

const Badge3D = React.memo(
  React.forwardRef<HTMLSpanElement, Badge3DProps>(
    ({ className, variant, size = 'sm', children, style, ...props }, ref) => {
      const config = variantConfig[variant];

      return (
        <span
          ref={ref}
          className={cn(
            'relative inline-flex items-center justify-center',
            'font-bold text-white select-none leading-none',
            config.bg,
            // 9px оставлен: нижняя ступень шкалы — 10 (t-nano), подъём растянет
            // плашку на карточке товара, где запаса по ширине нет.
            size === 'sm'
              ? 'text-[9px] px-2 py-[3px] rounded-pill'
              : 'text-xs px-3.5 py-1.5 rounded-pill',
            className
          )}
          style={{ boxShadow: config.shadow, ...style }}
          {...props}
        >
          {/* Блик — 3D выпуклость */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-[15%] top-[1px] h-[40%] rounded-pill"
            style={{
              background: `linear-gradient(180deg, ${config.glare} 0%, transparent 100%)`,
            }}
          />
          <span className="relative z-[1]">{children ?? config.label}</span>
        </span>
      );
    }
  )
);
Badge3D.displayName = 'Badge3D';

export { Badge3D };
